import {
  AnnouncementOutlined,
  ArrowForward,
  Call,
  CallMade,
  CallOutlined,
  CampaignOutlined,
  ErrorOutline,
  FiberManualRecord,
  Info,
  KeyboardArrowDown,
} from "@mui/icons-material";
import {
  Alert,
  Autocomplete,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  InputBase,
  MenuItem,
  Select,
  Snackbar,
  Switch,
  Tooltip,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import ErrorModal from "../modals/ErrorModal";
import SuccessModal from "../modals/SuccessModal";
import validator from "validator";
import { jwtDecode } from "jwt-decode";
import {
  formatPhoneNumber,
  formatPhoneNumberAsYouType,
  getPhoneOptionLabel,
} from "../utils/formatnumber";
import posthog from "posthog-js";
import decryptApi from "../utils/decryptApi";
import Front from "@frontapp/plugin-sdk";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { Device } from "@twilio/voice-sdk";
import OngoingCallModal from "../modals/OngoingCallModal";
import { PhoneOutgoing, TriangleAlert } from "lucide-react";
import { brand } from "../../theme/brandTokens";
import {
  completeOutboundCallNotification,
  readOutboundNotificationConfig,
  shouldSyncOutboundNotification,
} from "../../utils/outboundNotificationSettings";
import { getAppEnvironment } from "../../utils/appEnvironment";
import { getDefaultForwardingNumberForUser } from "../../utils/forwardNumberChannels";

const Dailer = () => {
  const [organization, setOrganization] = useState(
    JSON.parse(localStorage.getItem("organization"))
  );
  const [searchParams, setSearchParams] = useSearchParams();

  // Get recording and transcription settings for outbound calls
  const recordingSettings = organization?.recording_transcription_config?.M || {};
  const outboundRecordingPolicy = recordingSettings.outbound_recording?.S || "disallow";
  const outboundTranscriptionPolicy = recordingSettings.outbound_transcription?.S || "disallow";

  const outboundNotificationConfig = readOutboundNotificationConfig(organization);
  const outboundNotificationChannelId = outboundNotificationConfig.channel_id;
  const outboundNotificationPolicy = outboundNotificationConfig.policy || "allow";
  const hasNotificationChannel = !!outboundNotificationChannelId;

  // Get user ID for localStorage keys
  const userId = JSON.parse(localStorage.getItem("user"))?.userId?.S || "default";
  const recordingPreferenceKey = `recording_preference_${userId}`;
  const transcriptionPreferenceKey = `transcription_preference_${userId}`;
  const notificationPreferenceKey = `outbound_notification_preference_${userId}`;

  // State for user-controlled toggles (only used when policy is "allow")
  const [enableRecording, setEnableRecording] = useState(() => {
    const policy = recordingSettings.outbound_recording?.S || "disallow";
    if (policy === "require") {
      return true;
    } else if (policy === "disallow") {
      return false;
    } else {
      // Policy is "allow" - check localStorage for user preference
      const savedPreference = localStorage.getItem(recordingPreferenceKey);
      return savedPreference !== null ? savedPreference === "true" : true; // Default to true if no preference
    }
  });
  const [enableTranscription, setEnableTranscription] = useState(() => {
    const recPolicy = recordingSettings.outbound_recording?.S || "disallow";
    const transPolicy = recordingSettings.outbound_transcription?.S || "disallow";
    
    if (transPolicy === "require") {
      return true;
    } else if (transPolicy === "disallow" || recPolicy === "disallow") {
      return false;
    } else {
      // Policy is "allow" - check localStorage for user preference
      // First check if recording is enabled (either required or from localStorage)
      const recPolicyIsAllow = recPolicy === "allow";
      const recordingEnabled = recPolicy === "require" || 
        (recPolicyIsAllow && localStorage.getItem(recordingPreferenceKey) === "true");
      
      if (!recordingEnabled) {
        return false;
      }
      
      const savedPreference = localStorage.getItem(transcriptionPreferenceKey);
      return savedPreference !== null ? savedPreference === "true" : false; // Default to false if no preference
    }
  });
  const [showRecordingOptions, setShowRecordingOptions] = useState(false);
  const [enableNotification, setEnableNotification] = useState(() => {
    if (!hasNotificationChannel) {
      return false;
    }
    if (outboundNotificationPolicy === "require") {
      return true;
    }
    if (outboundNotificationPolicy === "allow") {
      const savedPreference = localStorage.getItem(notificationPreferenceKey);
      return savedPreference !== null ? savedPreference === "true" : true;
    }
    return false;
  });

  // Update transcription when recording changes (if transcription is allowed)
  useEffect(() => {
    if (!enableRecording && outboundTranscriptionPolicy === "allow") {
      setEnableTranscription(false);
      // Clear transcription preference when recording is disabled
      localStorage.removeItem(transcriptionPreferenceKey);
    }
  }, [enableRecording, outboundTranscriptionPolicy, transcriptionPreferenceKey]);

  // Save recording preference to localStorage when it changes (only if policy is "allow")
  useEffect(() => {
    if (outboundRecordingPolicy === "allow") {
      localStorage.setItem(recordingPreferenceKey, enableRecording.toString());
    }
  }, [enableRecording, outboundRecordingPolicy, recordingPreferenceKey]);

  // Save transcription preference to localStorage when it changes (only if policy is "allow")
  useEffect(() => {
    if (outboundTranscriptionPolicy === "allow" && outboundRecordingPolicy !== "disallow") {
      localStorage.setItem(transcriptionPreferenceKey, enableTranscription.toString());
    }
  }, [enableTranscription, outboundTranscriptionPolicy, outboundRecordingPolicy, transcriptionPreferenceKey]);

  useEffect(() => {
    if (outboundNotificationPolicy === "allow" && hasNotificationChannel) {
      localStorage.setItem(notificationPreferenceKey, enableNotification.toString());
    }
  }, [
    enableNotification,
    outboundNotificationPolicy,
    hasNotificationChannel,
    notificationPreferenceKey,
  ]);

  // Update states when organization changes
  useEffect(() => {
    const recPolicy = recordingSettings.outbound_recording?.S || "disallow";
    const transPolicy = recordingSettings.outbound_transcription?.S || "disallow";
    
    if (recPolicy === "require") {
      setEnableRecording(true);
    } else if (recPolicy === "disallow") {
      setEnableRecording(false);
    } else if (recPolicy === "allow") {
      // Load user preference from localStorage
      const savedPreference = localStorage.getItem(recordingPreferenceKey);
      if (savedPreference !== null) {
        setEnableRecording(savedPreference === "true");
      }
    }
    
    if (transPolicy === "require") {
      setEnableTranscription(true);
    } else if (transPolicy === "disallow" || recPolicy === "disallow") {
      setEnableTranscription(false);
    } else if (transPolicy === "allow" && recPolicy !== "disallow") {
      // Load user preference from localStorage, but only if recording is enabled
      if (recPolicy === "require" || enableRecording) {
        const savedPreference = localStorage.getItem(transcriptionPreferenceKey);
        if (savedPreference !== null) {
          setEnableTranscription(savedPreference === "true");
        } else {
          setEnableTranscription(false);
        }
      } else {
        setEnableTranscription(false);
      }
    }

    if (!hasNotificationChannel) {
      setEnableNotification(false);
    } else if (outboundNotificationPolicy === "require") {
      setEnableNotification(true);
    } else if (outboundNotificationPolicy === "allow") {
      const savedPreference = localStorage.getItem(notificationPreferenceKey);
      if (savedPreference !== null) {
        setEnableNotification(savedPreference === "true");
      } else {
        setEnableNotification(true);
      }
    } else {
      setEnableNotification(false);
    }
  }, [organization, recordingPreferenceKey, transcriptionPreferenceKey, enableRecording, notificationPreferenceKey, hasNotificationChannel, outboundNotificationPolicy]);

  async function decodeToken(token) {
    try {
      return await decryptApi(token);
    } catch (error) {
      // Log when Twilio token decrypt fails
      console.error("Invalid token:", error);
      return null;
    }
  }

  const [call_data, setCallData] = useState(() => {
    const org = JSON.parse(localStorage.getItem("organization"));
    const currentUserId = JSON.parse(localStorage.getItem("user"))?.userId?.S;
    const hasBrowserCalling = !!org?.twilio_integration?.M?.app_sid?.S;
    return {
      fromNumber: org?.twilio_integration?.M?.primary_phone?.S,
      forwarding_number: getDefaultForwardingNumberForUser(org, currentUserId),
      recipient: "",
      callMethod: hasBrowserCalling ? "browser_call" : "forwarding_device",
      call_user: JSON.parse(localStorage.getItem("user")).email.S,
    };
  });

  let latestContext;

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const subscribe = Front.contextUpdates.subscribe((context) => {
      latestContext = context;
      let link;

      latestContext.listMessages().then((response) => {
        if (response.results.length > 0) {
          const latestMessageIndex = response.results.length - 1;
          let body = response.results[latestMessageIndex].content.body;

          const path = body.match(/twilanswer\.com(\/[^\s"]+)/)?.[1];

          link = path;
        }

        if (latestContext.entryPoint) {
          if (
            latestContext.entryPoint &&
            latestContext.entryPoint.type == "CONTACT_HANDLE"
          ) {
            setCallData((prev) => ({
              ...prev,
              recipient: latestContext.entryPoint.data.handle,
            }));
          }
        }
      });
    });
    return () => {
      subscribe.unsubscribe();
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const recipientParam = params.get("recipient");
    if (recipientParam) {
      setCallData((prev) => ({ ...prev, recipient: recipientParam }));
    }
  }, [location.search]);

  const [err_msg, setErrorMsg] = useState(null);
  const [loader, setLoader] = useState(false);
  const [succeesModal, setSuccessModal] = useState(false);
  const [errorModal, setErrrorModal] = useState(false);
  const [from_numbers, setFromNumbers] = useState([
    {
      phoneNumber: JSON.parse(localStorage.getItem("organization")).twilio_integration.M.primary_phone.S,
      friendlyName: "Primary Phone Number",
    }
  ]);
  const [phoneNumbersLoaded, setPhoneNumbersLoaded] = useState(false);
  const [loadingPhoneNumbers, setLoadingPhoneNumbers] = useState(false);

  async function getIncomingPhoneNumbers() {
    // Don't fetch if already loaded
    if (phoneNumbersLoaded) {
      return;
    }
    
    setLoadingPhoneNumbers(true);
    setErrorMsg(null);
    const org = JSON.parse(localStorage.getItem("organization"));
    await axios
      .post(
        process.env.REACT_APP_BACKEND_URL + "twilio/get-incoming-phone-numbers",
        { organization_id: org.orgId.S }
      )
      .then((res) => {
        let data = res.data;
        if (res.data.length < 1) {
          setErrorMsg(
            "please add an incoming phone number in your twilio account"
          );
          setLoadingPhoneNumbers(false);
        } else {
          setFromNumbers(res.data);
          setPhoneNumbersLoaded(true);
          setLoadingPhoneNumbers(false);
        }
      })
      .catch((err) => {
        // Log for production diagnostics when fetching phone numbers fails
        console.error("Dailer: fetch phone numbers failed", err.response?.data || err.message || err);
        if (err.response && err.response.data) {
          setErrorMsg(err.response.data.message);
        }
        setLoadingPhoneNumbers(false);
      });
  }

  const call = async () => {
    let check = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}get-user-by-id`,
      {
        userId: JSON.parse(localStorage.getItem("user")).userId.S,
      }
    );

    if (check.status === 200) {
      const user___ = check.data.user;

      if (
        user___.relatedOrgs.L?.find(
          (e) =>
            e?.M.org_id?.S ===
            JSON.parse(localStorage.getItem("organization")).orgId.S
        )
      ) {
        if (validator.isMobilePhone(call_data.recipient)) {
          try {
            setLoader(true);

            // Determine recording and transcription based on policy and user selection
            const shouldRecord = outboundRecordingPolicy === "require" || 
                                (outboundRecordingPolicy === "allow" && enableRecording);
            const shouldTranscribe = outboundTranscriptionPolicy === "require" || 
                                    (outboundTranscriptionPolicy === "allow" && enableTranscription && shouldRecord);
            const syncNotification = shouldSyncOutboundNotification({
              channelId: outboundNotificationChannelId,
              policy: outboundNotificationPolicy,
              userEnabled: enableNotification,
            });

            const orgId = JSON.parse(localStorage.getItem("organization")).orgId.S;
            const user = JSON.parse(localStorage.getItem("user"));

            // Create call record first so we have a call_id for Twilio
            let call_ = await axios.post(
              process.env.REACT_APP_BACKEND_URL + "call/create-call",
              {
                organizationId: JSON.parse(localStorage.getItem("organization"))
                  .orgId.S,
                startTime: Math.floor(Date.now() / 1000),
                toNumber: call_data.recipient,
                fromNumber: call_data.fromNumber,
                action: "Outbound Call",
                action_details: call_data.forwarding_number,
                has_vm: false,
                call_user: `${user.fName.S} ${user.lName.S}`,
                call_user_id: user.userId.S,
                record: shouldRecord,
                transcribe: shouldTranscribe,
                ...(syncNotification
                  ? { channel_id: outboundNotificationChannelId }
                  : {}),
              }
            );

            const callId = call_.data?.call?.callId?.S;

            const twilioPayload = {
              fromNumber: call_data.fromNumber,
              forwarding_number: call_data.forwarding_number,
              recipient: call_data.recipient,
              callMethod: call_data.callMethod,
              organization_id: orgId,
              record: shouldRecord,
              transcribe: shouldTranscribe,
            };
            if (callId) {
              twilioPayload.call_id = callId;
            }

            let twilio_call = await axios.post(
              process.env.REACT_APP_BACKEND_URL + "twilio/create-call",
              twilioPayload
            );
            let call_data_ = twilio_call.data.call;

            setSuccessModal(true);
            posthog.capture("Dailed a phone call", {
              email: JSON.parse(localStorage.getItem("user")).email.S,
              userId: JSON.parse(localStorage.getItem("user")).userId.S,
            });
            setLoader(false);
          } catch (error) {
            // Log for production diagnostics when placing call fails
            console.error("Dailer: place call failed", error.response?.data || error.message || error);
            setErrrorModal(true);
            const status = error?.response?.status;
            const apiError = error?.response?.data?.error;
            const apiMessage = error?.response?.data?.message;
            if (
              status === 401 &&
              typeof apiError === "string" &&
              apiError.toLowerCase().includes("authorization header")
            ) {
              setErrorMsg(
                "We ran into an authentication issue and can’t verify your session. Please log out and log back in."
              );
            } else if (error.response && error.response.data) {
              setErrorMsg([apiError, apiMessage].filter(Boolean).join(" "));
            }
            setLoader(false);
          }
        } else {
          setErrorMsg("Recipient number is not valid");
          setErrrorModal(true);
        }
      } else {
        window.location.reload();
      }
    }
  };
  const handleNavigation = (to) => {
    window.location.assign(to);
  };

  //new

  const [device, setdevice] = useState();
  const [twilioCredsSnackbarOpen, setTwilioCredsSnackbarOpen] = useState(false);

  const setUpDevice = async () => {
    // Don't set up device if it already exists and is registered
    if (device && device.state === "registered") {
      return;
    }

    const org = JSON.parse(localStorage.getItem("organization"));
    if (!org?.orgId?.S) {
      setTwilioCredsSnackbarOpen(true);
      return;
    }

    try {
      const response = await axios.post(
        process.env.REACT_APP_BACKEND_URL + "twilio/token",
        { organization_id: org.orgId.S }
      );

      // Make sure backend returns { token: '...' }
      const token = response.data.token || response.data;

      if (!token || typeof token !== "string") {
        // Log when backend returns invalid token shape
        console.error("Token must be a string, but got:", typeof token, token);
        setTwilioCredsSnackbarOpen(true);
        return;
      }

      // Destroy existing device if it exists
      if (device) {
        try {
          device.destroy();
        } catch (e) {
          // Ignore destroy errors during setup
        }
      }

      const newDevice = new Device(token, {
        debug: true,
      });

      // Set up error handlers before registering
      newDevice.on("ready", () => {});

      newDevice.on("error", (error) => {
        // Log for production diagnostics when Twilio Device fails
        console.error("Twilio Device error:", error);
        if (error.code === 20101) {
          console.error("Access token invalid - credentials may be incorrect or expired");
          setdevice(null);
          setTwilioCredsSnackbarOpen(true);
        }
      });

      await newDevice.register();

      setdevice(newDevice);
    } catch (error) {
      // Log for production diagnostics when device setup fails
      console.error("Error setting up device:", error);
      if (error?.response?.data) {
        console.error("Backend error:", error.response.data);
      }
      setTwilioCredsSnackbarOpen(true);
    }
  };

  useEffect(() => {
    // Only set up device when browser call method is selected
    // and only once per page load
    let hasSetUp = false;
    const handleUserGesture = () => {
      if (call_data.callMethod === "browser_call" && !hasSetUp) {
        hasSetUp = true;
        setUpDevice();
      }
      window.removeEventListener("click", handleUserGesture);
    };

    window.addEventListener("click", handleUserGesture);
    
    return () => {
      window.removeEventListener("click", handleUserGesture);
      // Cleanup: destroy device when component unmounts
      if (device) {
        try {
          device.destroy();
        } catch (e) {
          // Ignore destroy errors on unmount
        }
      }
    };
  }, [call_data.callMethod]);

  const [openOngoingCallModal, setOpenOngoingCallModal] = useState(false);
  const [conn, setConn] = useState();
  const outboundCallCompleteRef = useRef(null);
  const outboundCallCompleteNotifiedRef = useRef(false);

  async function makeCall() {
    if (!device || device.state !== "registered") {
      // Log when user tries to call before device is ready
      console.error("Twilio device not ready");
      return;
    }

    // Determine recording and transcription based on policy and user selection
    const shouldRecord = outboundRecordingPolicy === "require" || 
                        (outboundRecordingPolicy === "allow" && enableRecording);
    const shouldTranscribe = outboundTranscriptionPolicy === "require" || 
                            (outboundTranscriptionPolicy === "allow" && enableTranscription && shouldRecord);
    const syncNotification = shouldSyncOutboundNotification({
      channelId: outboundNotificationChannelId,
      policy: outboundNotificationPolicy,
      userEnabled: enableNotification,
    });

    const orgId = JSON.parse(localStorage.getItem("organization")).orgId.S;
    outboundCallCompleteNotifiedRef.current = false;

    let call_ = await axios.post(
      process.env.REACT_APP_BACKEND_URL + "call/create-call",
      {
        organizationId: orgId,
        startTime: Math.floor(Date.now() / 1000),
        toNumber: call_data.recipient,
        fromNumber: call_data.fromNumber,
        action: "Outbound Call",
        action_details: "Browser Call",
        has_vm: false,
        record: shouldRecord,
        transcribe: shouldTranscribe,
        ...(syncNotification
          ? { channel_id: outboundNotificationChannelId }
          : {}),
      }
    );

    const callId = call_.data?.call?.callId?.S;

    if (syncNotification && callId && outboundNotificationChannelId) {
      outboundCallCompleteRef.current = {
        organizationId: orgId,
        callId,
        channel_id: outboundNotificationChannelId,
      };
    } else {
      outboundCallCompleteRef.current = null;
    }

    const notifyOutboundCallComplete = async () => {
      const payload = outboundCallCompleteRef.current;
      if (!payload || outboundCallCompleteNotifiedRef.current) return;
      outboundCallCompleteNotifiedRef.current = true;
      try {
        await completeOutboundCallNotification(payload);
      } catch (error) {
        console.error(
          "Dailer: complete-call failed",
          error.response?.data || error.message || error
        );
      } finally {
        outboundCallCompleteRef.current = null;
      }
    };

    const connection = await device.connect({
      params: {
        To: call_data.recipient,
        Caller: call_data.fromNumber,
        Record: shouldRecord ? "true" : "false",
        Transcribe: shouldTranscribe ? "true" : "false",
        call_id: callId ?? "",
        orgId: orgId,
        environment: getAppEnvironment(),
      },
    });

    ["disconnect", "cancel", "reject"].forEach((event) => {
      connection.on(event, notifyOutboundCallComplete);
    });

    setConn(connection);

    setOpenOngoingCallModal(true);

    device.on("disconnect", () => {
      setOpenOngoingCallModal(false);
    });

    device.on("cancel", () => {
      setOpenOngoingCallModal(false);
    });

    device.on("error", (error) => {
      // Log for production diagnostics when Twilio call fails
      console.error("Twilio call error:", error);
      setOpenOngoingCallModal(false);
    });
  }
  useEffect(() => {
    const recipientNumber = searchParams.get("recipient_number");
  
    if (!recipientNumber) return;
  
    // 1. Save to state
    setCallData((prev) => ({
      ...prev,
      recipient: decodeURIComponent(recipientNumber),
    }));
  
    // 2. Remove from URL
    const params = new URLSearchParams(searchParams);
    params.delete("recipient_number");
    setSearchParams(params, { replace: true });
  
  }, [searchParams, setSearchParams]);
  

  return (
    <div className="pt-7 px-4">
      <div className="lg:text-[22px] text-[20px] font-bold mb-4 lg:mt-0 mt-8 ">
        Dialer
      </div>

      <OngoingCallModal
        open={openOngoingCallModal}
        setOpen={setOpenOngoingCallModal}
        call_data={call_data}
        device={device}
        connection={conn}
      />
      <SuccessModal
        open={succeesModal}
        setOpen={setSuccessModal}
        call_data={call_data}
        setCallData={setCallData}
        decodeToken={decodeToken}
      />

      <ErrorModal
        open={errorModal}
        setOpen={setErrrorModal}
        call_data={call_data}
        setCallData={setCallData}
        err_msg={err_msg}
        setErrorMsg={setErrorMsg}
        decodeToken={decodeToken}
      />

      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={twilioCredsSnackbarOpen}
        autoHideDuration={8000}
        onClose={() => setTwilioCredsSnackbarOpen(false)}
      >
        <Alert
          onClose={() => setTwilioCredsSnackbarOpen(false)}
          severity="error"
          sx={{ width: "100%" }}
        >
          There’s a problem with your Twilio API credentials.{" "}
          <Link
            to="/dashboard/settings/twilio-integration"
            style={{ fontWeight: 600, textDecoration: "underline" }}
            onClick={() => setTwilioCredsSnackbarOpen(false)}
          >
            Update in Settings
          </Link>
        </Alert>
      </Snackbar>

      <div className="flex justify-center">
        <div className="bg-white rounded-[13px]  lg:w-[640px] w-full  lg:px-14 px-5 py-5 flex justify-center border-[1px] border-gray-200">
          <div className="space-y-5 bg-white w-full ">
            <div className="lg:flex w-full items-center lg:space-x-10 ">
              <div className="w-full">
                <div className="lg:text-[14px] text-[14px] font-semibold flex items-center mb-2">
                  Recipient Phone Number
                </div>
                <InputBase
                  value={call_data.recipient}
                  placeholder="(555) 123-4567"
                  onChange={(e) => {
                    setCallData({
                      ...call_data,
                      recipient: formatPhoneNumberAsYouType(e.target.value),
                    });
                  }}
                  sx={{
                    bgcolor: "white",
                    width: "100%",
                    px: 2,
                    py: 1,
                    fontSize: "16px",
                    border: "1px solid #D1D5DB",
                    borderRadius: "8px",
                  }}
                />
              </div>
            </div>
            <div className="lg:flex w-full items-center lg:space-x-10">
              <div className="w-full">
                <div className="lg:text-[14px] text-[14px] font-semibold flex items-center mb-2">
                  Caller ID
                </div>

                <Autocomplete
                  value={

                    from_numbers?.find(
                      (n) => n.phoneNumber === call_data.fromNumber
                    ) || null
                  }
                  onChange={(e, newValue) => {
                    setCallData({
                      ...call_data,
                      fromNumber: newValue ? newValue.phoneNumber : "",
                    });
                  }}
                  onOpen={() => {
                    // Fetch phone numbers when dropdown opens (only once)
                    if (!phoneNumbersLoaded && !loadingPhoneNumbers) {
                      getIncomingPhoneNumbers();
                    }
                  }}
                  options={from_numbers || []}
                  loading={loadingPhoneNumbers}
                  getOptionLabel={(option) =>
                    getPhoneOptionLabel(option)
                  }
                  renderInput={(params) => (
                    <InputBase
                      {...params.InputProps}
                      inputProps={params.inputProps}
                      placeholder="Select Caller ID"
                      sx={{
                        bgcolor: "white",
                        width: "100%",
                        px: 2,
                        py: 1.2,
                        fontSize: "16px",
                        border: "1px solid #D1D5DB",
                        borderRadius: "8px",
                      }}
                      endAdornment={
                        <>
                          {loadingPhoneNumbers ? (
                            <CircularProgress size={20} sx={{ mr: 1 }} />
                          ) : (
                            params.InputProps.endAdornment
                          )}
                        </>
                      }
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props}>
                      {getPhoneOptionLabel(option)}
                    </li>
                  )}
                  popupIcon={<KeyboardArrowDown />}
                  disableClearable
                  fullWidth
                />
              </div>
            </div>

            <div className="lg:flex w-full items-center lg:space-x-10  ">
              <div className="w-full">
                <div className="lg:text-[14px] text-[14px] font-semibold flex items-center mb-2">
                  Call Method
                </div>
                <div className="lg:flex items-center  lg:space-x-3 lg:space-y-0 space-y-3 ">
                  <div
                    onClick={() => {
                      if (
                        !JSON.parse(localStorage.getItem("organization"))
                          .twilio_integration?.M?.app_sid?.S
                      ) {
                        document.getElementById(
                          "nobrowsercallfunctionality"
                        ).style.display = "block";
                      } else {
                        setCallData((prev) => ({
                          ...prev,
                          callMethod: "browser_call",
                        }));
                      }
                    }}
                    className={
                      call_data.callMethod == "browser_call"
                        ? "cursor-pointer text-brand-primary w-full border-brand-primary bg-brand-mist border-[1.8px] px-4 py-2 rounded-md flex items-center space-x-3"
                        : "cursor-pointer w-full border-gray-100 border-[1.8px] px-4 py-2 rounded-md flex items-center space-x-3"
                    }
                  >
                    <div>
                      <CampaignOutlined
                        sx={
                          call_data.callMethod == "browser_call"
                            ? { color: brand.primary }
                            : { color: "#e5e5e5" }
                        }
                      />
                    </div>

                    <div>
                      <div className="lg:text-[16px] text-[14px] font-bold ">
                        Browser Call
                      </div>
                      <div className="lg:text-[14px] text-[12px]  text-gray-400">
                        Call from browser
                      </div>
                      <div
                        className="text-[13px] lg:text-[13px] text-yellow-600 "
                        hidden={
                          !JSON.parse(localStorage.getItem("organization"))
                            ?.twilio_integration?.M?.app_sid?.S
                            ? false
                            : true
                        }
                        id="nobrowsercallfunctionality"
                      >
                        <span>
                          <Info sx={{ fontSize: "" }} />
                        </span>{" "}
                        You don’t have browser calling configured
                      </div>
                    </div>
                  </div>

                  <div
                    onClick={() => {
                      setCallData((prev) => ({
                        ...prev,
                        callMethod: "forwarding_device",
                      }));
                    }}
                    className={
                      call_data.callMethod == "forwarding_device"
                        ? "cursor-pointer text-brand-primary w-full border-brand-primary bg-brand-mist border-[1.8px] px-4 py-2 rounded-md flex items-center space-x-3"
                        : "cursor-pointer w-full border-gray-100  border-[1.8px] px-4 py-2 rounded-md flex items-center space-x-3"
                    }
                  >
                    <div>
                      <CallOutlined
                        sx={
                          call_data.callMethod == "forwarding_device"
                            ? { color: brand.primary }
                            : { color: "#e5e5e5" }
                        }
                      />
                    </div>

                    <div>
                      <div className="lg:text-[16px] text-[14px] font-bold ">
                        Forwarding Device
                      </div>
                      <div className="lg:text-[14px] text-[12px]  text-gray-400">
                        Call your device first
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {call_data?.callMethod == "forwarding_device" && (
              <div className="lg:flex justify-end items-center lg:space-x-10 ">
                <div className="w-full">
                  <div className="lg:text-[14px] text-[14px] font-semibold flex items-center mb-2">
                    Forwarding Number
                  </div>
                  <div className="text-[12px] text-gray-500 mb-2">
                    Twilanswer will call this number first, then connect you to your recipient
                  </div>
                  <Select
                    IconComponent={KeyboardArrowDown}
                    fullWidth
                    defaultValue="Forward w/ VM"
                    value={call_data.forwarding_number}
                    onChange={(e) => {
                      setCallData({
                        ...call_data,
                        forwarding_number: e.target.value,
                      });
                    }}
                    className="mb-2 bg-white"
                    sx={{
                      fontSize: "16px",
                      padding: "16px 1px", // Adjust padding here
                      backgroundColor: "white",
                      "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                      "& .MuiSelect-select": {
                        padding: "16px ", // Adjust padding for the text inside Select
                        border: "1px solid #D1D5DB",
                        borderRadius: "8px",
                        backgroundColor: "white",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        border: "none",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        border: "none",
                      },
                    }}
                  >
                    {JSON.parse(localStorage.getItem("organization"))
                      .channels?.L.filter(
                        (e) => e.M.type.S === "forward_number"
                      )
                      .map((item, index) => {
                        return (
                          <MenuItem value={item.M.value.S}>
                            {formatPhoneNumber(item.M.value.S)} -{" "}
                            {item.M.label.S}
                          </MenuItem>
                        );
                      })}

                    <MenuItem
                      sx={{
                        textAlign: "center",
                        textTransform: "none",
                        bgcolor: "white",
                        color: brand.primary,
                        px: "2px",
                        borderRadius: "7px",
                        fontSize: { lg: "14px", xs: "13px" },
                        width: "100%",
                      }}
                      onClick={() => {
                        handleNavigation(
                          "/dashboard/channels?channeltype=forward_number"
                        );
                      }}
                    >
                      <div className="text-center w-full">
                        {" "}
                        Add Phone Number
                      </div>
                    </MenuItem>
                  </Select>
                </div>
              </div>
            )}

            {call_data?.callMethod == "browser_call" && 
              !JSON.parse(localStorage.getItem("organization"))
                ?.twilio_integration?.M?.app_sid?.S && (
              <div className="border-[1px] border-[#FFEF8A] text-[#854D0F] bg-[#FEFCE8] px-6 py-2 ">
                <div className="flex items-center text-[14px] font-bold text-[#854D0F] ">
                  {" "}
                  <TriangleAlert className="text-[#FACB14] w-[16px] mr-2" />{" "}
                  Browser Calling Not Configured{" "}
                </div>
                <div className="text-[#854D0F] text-[14px] my-2 ml-6">
                  {" "}
                  Browser calling requires additional setup in your organization
                  settings. Please configure your API credentials and TwiML App
                  to enable browser calling.
                </div>
                <div 
                  className="text-[13px] bg-[#FFEF8A] text-[#854D0F] w-fit px-1 rounded-[2px] ml-6 cursor-pointer hover:bg-[#d5c13f]"
                  onClick={() => navigate("/dashboard/settings/organization")}
                >
                  {" "}
                  Go To Settings <ArrowForward />{" "}
                </div>
              </div>
            )}

            <div className="flex justify-center items-center gap-3 pt-6 pb-4">
              {/* Recording Icon Button */}
              <div className="relative">
                <Tooltip
                  title={
                    outboundRecordingPolicy === "disallow"
                      ? "Recording disabled by organization policy"
                      : outboundRecordingPolicy === "require"
                      ? "Recording required"
                      : enableRecording
                      ? "Recording enabled - Click to change"
                      : "Recording disabled - Click to change"
                  }
                  arrow
                >
                  <button
                    onClick={() => setShowRecordingOptions(!showRecordingOptions)}
                    className={`p-2 rounded-lg border transition-colors ${
                      outboundRecordingPolicy === "require" || enableRecording
                        ? "border-brand-primary bg-brand-mist text-brand-primary"
                        : "border-gray-300 bg-white text-gray-400 hover:border-gray-400"
                    } ${outboundRecordingPolicy === "disallow" ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}
                    disabled={outboundRecordingPolicy === "disallow"}
                  >
                    <FiberManualRecord
                      sx={{
                        fontSize: "24px",
                        color: outboundRecordingPolicy === "require" || enableRecording ? brand.primary : "#D1D5DB",
                      }}
                    />
                  </button>
                </Tooltip>

                {/* Recording Options Dropdown */}
                {showRecordingOptions && (
                  <>
                    {/* Backdrop to close dropdown when clicking outside */}
                    <div
                      className="fixed inset-0 z-[5]"
                      onClick={() => setShowRecordingOptions(false)}
                    />
                    <div className="absolute bottom-full left-0 mb-2 bg-white border border-gray-200 rounded-lg shadow-lg p-4 min-w-[250px] z-[10]">
                      <div className="text-sm font-semibold text-gray-700 mb-3">
                        Call Options
                      </div>
                      
                      {/* Recording Toggle */}
                      <div className="mb-3 flex items-center justify-between">
                        <span className="text-[14px] text-gray-700">
                          Record Call
                        </span>
                        {outboundRecordingPolicy === "disallow" ? (
                          <Tooltip
                            title="Call recording is disabled by your organization's policy. Contact your administrator to enable recording."
                            arrow
                          >
                            <span>
                              <Switch
                                checked={false}
                                disabled={true}
                                sx={{
                                  "& .MuiSwitch-switchBase.Mui-disabled": {
                                    color: "#D1D5DB",
                                  },
                                  "& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track": {
                                    backgroundColor: "#E5E7EB",
                                  },
                                }}
                              />
                            </span>
                          </Tooltip>
                        ) : outboundRecordingPolicy === "require" ? (
                          <Tooltip
                            title="Call recording is required by your organization's policy and cannot be disabled."
                            arrow
                          >
                            <span>
                              <Switch
                                checked={true}
                                disabled={true}
                                sx={{
                                  "& .MuiSwitch-switchBase.Mui-checked": {
                                    color: brand.primary,
                                  },
                                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                                    backgroundColor: brand.primary,
                                  },
                                }}
                              />
                            </span>
                          </Tooltip>
                        ) : (
                          <Switch
                            checked={enableRecording}
                            onChange={(e) => {
                              setEnableRecording(e.target.checked);
                              if (!e.target.checked) {
                                setEnableTranscription(false);
                              }
                            }}
                            sx={{
                              "& .MuiSwitch-switchBase.Mui-checked": {
                                color: brand.primary,
                              },
                              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                                backgroundColor: brand.primary,
                              },
                            }}
                          />
                        )}
                      </div>

                      {/* Transcription Toggle */}
                      <div className="flex items-center justify-between">
                        <span className={`text-[14px] ${
                          outboundTranscriptionPolicy === "disallow" || outboundRecordingPolicy === "disallow"
                            ? "text-gray-400"
                            : "text-gray-700"
                        }`}>
                          Transcribe Call
                        </span>
                        {outboundTranscriptionPolicy === "disallow" || outboundRecordingPolicy === "disallow" ? (
                          <Tooltip
                            title={
                              outboundRecordingPolicy === "disallow"
                                ? "Transcription requires call recording to be enabled. Recording is currently disabled by your organization's policy."
                                : "Call transcription is disabled by your organization's policy. Contact your administrator to enable transcription."
                            }
                            arrow
                          >
                            <span>
                              <Switch
                                checked={false}
                                disabled={true}
                                sx={{
                                  "& .MuiSwitch-switchBase.Mui-disabled": {
                                    color: "#D1D5DB",
                                  },
                                  "& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track": {
                                    backgroundColor: "#E5E7EB",
                                  },
                                }}
                              />
                            </span>
                          </Tooltip>
                        ) : outboundTranscriptionPolicy === "require" ? (
                          <Tooltip
                            title="Call transcription is required by your organization's policy and cannot be disabled."
                            arrow
                          >
                            <span>
                              <Switch
                                checked={true}
                                disabled={true}
                                sx={{
                                  "& .MuiSwitch-switchBase.Mui-checked": {
                                    color: brand.primary,
                                  },
                                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                                    backgroundColor: brand.primary,
                                  },
                                }}
                              />
                            </span>
                          </Tooltip>
                        ) : (
                          <Switch
                            checked={enableTranscription}
                            onChange={(e) => setEnableTranscription(e.target.checked)}
                            disabled={!enableRecording}
                            sx={{
                              "& .MuiSwitch-switchBase.Mui-checked": {
                                color: brand.primary,
                              },
                              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                                backgroundColor: brand.primary,
                              },
                              "& .MuiSwitch-switchBase.Mui-disabled": {
                                color: "#D1D5DB",
                              },
                              "& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track": {
                                backgroundColor: "#E5E7EB",
                              },
                            }}
                          />
                        )}
                      </div>

                      {/* Sync Notification Toggle */}
                      <div className="mt-3 flex items-center justify-between">
                        <span
                          className={`text-[14px] ${
                            !hasNotificationChannel
                              ? "text-gray-400"
                              : "text-gray-700"
                          }`}
                        >
                          Sync Notification
                        </span>
                        {!hasNotificationChannel ? (
                          <Tooltip
                            title="No outbound notification channel is configured. Ask your administrator to set one in Organization settings."
                            arrow
                          >
                            <span>
                              <Switch
                                checked={false}
                                disabled={true}
                                sx={{
                                  "& .MuiSwitch-switchBase.Mui-disabled": {
                                    color: "#D1D5DB",
                                  },
                                  "& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track": {
                                    backgroundColor: "#E5E7EB",
                                  },
                                }}
                              />
                            </span>
                          </Tooltip>
                        ) : outboundNotificationPolicy === "require" ? (
                          <Tooltip
                            title="Outbound call notifications are required by your organization's policy."
                            arrow
                          >
                            <span>
                              <Switch
                                checked={true}
                                disabled={true}
                                sx={{
                                  "& .MuiSwitch-switchBase.Mui-checked": {
                                    color: brand.primary,
                                  },
                                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                                    backgroundColor: brand.primary,
                                  },
                                }}
                              />
                            </span>
                          </Tooltip>
                        ) : (
                          <Tooltip
                            title="Send call details to the configured notification channel."
                            arrow
                          >
                            <span>
                              <Switch
                                checked={enableNotification}
                                onChange={(e) =>
                                  setEnableNotification(e.target.checked)
                                }
                                sx={{
                                  "& .MuiSwitch-switchBase.Mui-checked": {
                                    color: brand.primary,
                                  },
                                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                                    backgroundColor: brand.primary,
                                  },
                                }}
                              />
                            </span>
                          </Tooltip>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
              {/* <button onClick={setUpDevice}>
  Enable Call
</button> */}
              <Button
                sx={{
                  textTransform: "none",
                  bgcolor: "green",
                  color: "white",
                  py: "10px",
                  px: "20px",
                  borderRadius: "7px",
                  fontSize: { lg: "17px", xs: "14px" },
                }}
                onClick={() => {
                  if (call_data?.recipient?.length <= 1) {
                  } else {
                    if (!loader) {
                      if (call_data.callMethod == "forwarding_device") {
                        call();
                      } else {
                        makeCall();
                      }
                    }
                  }
                }}
                //disabled={ ! (/^\(\d{3}\) \d{3}-\d{4}$/.test(call_data.recipient))}
              >
                {loader ? (
                  <CircularProgress
                    size={"1.2rem"}
                    sx={{ color: "white", animationDuration: "0.7s" }}
                  />
                ) : (
                  <>
                    <PhoneOutgoing className="w-[20px] mr-2  " />
                    Start Call
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dailer;
